"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { AlertTriangleIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { useRouter } from "@/lib/navigation";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { DELETE_DASHBOARD_NAME } from "./constants";
import type { DeleteDashboardInput } from "./schema";

const DashboardQuery = graphql(`
  query DeleteDashboardConfirmQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on Dashboard {
        id
        name
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

/**
 * ダッシュボード削除のシンプルな確認 UI。
 * 対象 ID を表示し、危険操作であることを明示して承認可否を求める。
 */
export function DeleteDashboardConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const router = useRouter();
  const toolName = isToolUIPart(part) ? getToolName(part) : null;
  const isTarget = toolName === DELETE_DASHBOARD_NAME;
  const isInputAvailable = part.state === "input-available";
  const input = (part.input ?? {}) as Partial<DeleteDashboardInput>;
  const dashboardId = input.dashboardId ?? "";

  const [{ data, fetching, error }] = useQuery({
    query: DashboardQuery,
    variables: { id: dashboardId },
    pause: !isTarget || !isInputAvailable || !dashboardId,
  });

  if (!isTarget || !isInputAvailable) return null;

  const dashboard = data?.node?.__typename === "Dashboard" ? data.node : null;
  const pending = fetching && !dashboard;
  const loadFailed = !!error || (!pending && !dashboard);

  const handleSuccess = () => {
    router.refresh();
  };

  return (
    <div className="p-4 text-sm space-y-4">
      <div>
        <div className="mb-2 font-medium">
          <Trans>Do you want to delete this dashboard?</Trans>
        </div>
        <div className="rounded border bg-muted/30 p-3">
          <div className="text-xs text-muted-foreground">
            <Trans>Dashboard</Trans>
          </div>
          {pending ? (
            <Skeleton className="mt-1 h-4 w-36 bg-gray-400 dark:bg-gray-600" />
          ) : (
            <div className="mt-1 font-semibold">{dashboard?.name ?? "-"}</div>
          )}
        </div>
        {loadFailed ? (
          <Alert variant="destructive" className="mt-2 text-xs">
            <AlertTitle>
              <Trans>Unable to load the dashboard</Trans>
            </AlertTitle>
            <AlertDescription>
              <Trans>
                It may already be deleted. Please reload and confirm before
                retrying.
              </Trans>
            </AlertDescription>
          </Alert>
        ) : null}
      </div>
      {!loadFailed ? (
        <Alert variant="destructive" className="text-xs">
          <AlertTriangleIcon className="h-4 w-4" />
          <AlertTitle>
            <Trans>This action is irreversible</Trans>
          </AlertTitle>
          <AlertDescription>
            <Trans>
              Deleting a dashboard removes its cards and saved configuration for
              everyone in the workspace.
            </Trans>
          </AlertDescription>
        </Alert>
      ) : null}
      <div className="flex gap-2">
        {loadFailed ? (
          <Button size="sm" variant="secondary" disabled>
            <Trans>Delete</Trans>
          </Button>
        ) : (
          <ApproveButton
            toolCallId={part.toolCallId}
            toolName={toolName ?? DELETE_DASHBOARD_NAME}
            addToolResult={addToolResult}
            sendMessage={sendMessage}
            variant="destructive"
            onSuccess={handleSuccess}
          >
            <Trans>Delete</Trans>
          </ApproveButton>
        )}
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={toolName ?? DELETE_DASHBOARD_NAME}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
